// Resolves the active giveaway products from the OC API and caches them, so any
// page on the storefront can render them without its own round trip.
//
// Endpoint: GET /endpoint/products?nonSpecific=true
//   - Public. It is allow-listed unauthenticated in oc-api's authJWT checkPaths,
//     and `cors()` is registered bare, so `Access-Control-Allow-Origin: *`.
//   - `nonSpecific=true` is REQUIRED. Without it the handler branches on an
//     `x-access-token` header and merges per-user `activeEntries` into each
//     record. We never send that header, but pinning the flag keeps the response
//     shape stable if anything ever attaches one.
//   - Do NOT use /endpoint/products/active or /endpoint/product/:id — neither is
//     allow-listed, so both 403 for an anonymous browser.
//   - The endpoint returns EVERY product, active and inactive, so the isActive
//     filter below is load-bearing, not a nicety.
//
// IMPORTANT — this differs from oc-entry-multiplier.js in one way that matters:
// there, expiry is re-derived at READ time from the promo's own window, so a
// stale cache can never resurrect an ended promo. Here "active" is the server's
// `isActive` boolean; oc-api filters on it everywhere (assignEntries.js,
// entries.route.js) and uses startDate/endDate only for sorting and countdown
// copy. Expiry therefore CANNOT be recomputed client-side, which makes the TTL a
// real staleness guard rather than just a propagation delay. Two consequences:
// keep the TTL short, and re-validate the submitted _id server-side before
// assigning entries — never trust the cached list as authorization.
//
// Public API:
//   getAll()     -> array of active giveaways, sorted for display (sync)
//   getById(id)  -> one giveaway or null (sync)
//   isStale()    -> boolean, whether the cache is past its TTL
//   refresh()    -> Promise, forces a refetch
//   ready()      -> Promise, resolves once any in-flight fetch settles
//
// Fires `oc:giveaways:updated` on `document` after a fetch changes the cache, so
// UI built from a cold cache can repaint.
window.OCGiveaways = (function () {
  const ENDPOINT = "https://api.onecountry.com/endpoint/products?nonSpecific=true";
  const STORAGE_KEY = "oc_giveaways";
  const TTL_MS = 5 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 4000;

  // Local preview only — MUST be null in production. Writes through the cache
  // exactly like a real response, so every downstream read takes its normal
  // path. After changing it, run: localStorage.removeItem('oc_giveaways')
  //
  //   [{ _id: "demo-1", title: "2026 Ford Bronco", isActive: true,
  //      imgUrl: "https://…/bronco.jpg", endDate: "2026-12-01T00:00:00.000Z" }]
  const STUB_GIVEAWAYS = null;

  let memoryCache = null; // fallback when localStorage is unavailable
  let inFlight = null;

  function readCache() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // private mode, quota, or malformed JSON — fall through to memory
    }
    return memoryCache;
  }

  function writeCache(payload) {
    memoryCache = payload;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // memoryCache still serves this pageload
    }
  }

  // Mirrors oc-api's definition exactly. Deliberately does NOT also test
  // endDate: no server-side query filters on dates, so adding one here would
  // hide giveaways the Orders API still accepts entries for.
  function isActive(giveaway) {
    return !!giveaway && giveaway.isActive === true && !!giveaway._id;
  }

  // `sortPreference` is the merchandiser's explicit ordering; records without
  // one fall to the back, ordered by whichever ends soonest.
  function sortForDisplay(list) {
    return list.slice().sort(function (a, b) {
      const aPref = typeof a.sortPreference === "number" ? a.sortPreference : Infinity;
      const bPref = typeof b.sortPreference === "number" ? b.sortPreference : Infinity;
      if (aPref !== bPref) return aPref - bPref;
      const aEnd = a.endDate ? new Date(a.endDate).getTime() : Infinity;
      const bEnd = b.endDate ? new Date(b.endDate).getTime() : Infinity;
      return aEnd - bEnd;
    });
  }

  function fetchGiveaways() {
    if (inFlight) return inFlight;
    if (STUB_GIVEAWAYS) {
      writeCache({ fetchedAt: Date.now(), giveaways: STUB_GIVEAWAYS.filter(isActive) });
      return Promise.resolve(STUB_GIVEAWAYS);
    }
    const controller = new AbortController();
    const timer = setTimeout(function () {
      controller.abort();
    }, FETCH_TIMEOUT_MS);
    inFlight = fetch(ENDPOINT, { signal: controller.signal })
      .then(function (res) {
        return res.ok ? res.json() : Promise.reject(res.status);
      })
      .then(function (products) {
        // Filter before caching so the stored payload stays small — the raw
        // response is un-projected Mongo documents including description and
        // legal copy for every product ever created.
        const list = Array.isArray(products) ? products.filter(isActive) : [];
        writeCache({ fetchedAt: Date.now(), giveaways: list });
        document.dispatchEvent(new CustomEvent("oc:giveaways:updated"));
        return list;
      })
      .catch(function () {
        return null; // a giveaway lookup must never break a pageload
      })
      .finally(function () {
        clearTimeout(timer);
        inFlight = null;
      });
    return inFlight;
  }

  // Synchronous — safe to call from a click handler or a render loop.
  function getAll() {
    const cache = readCache();
    if (!cache || !Array.isArray(cache.giveaways)) return [];
    // Re-filtered on read: a cache written by an older build of this file, or by
    // hand during preview, may hold inactive records.
    return sortForDisplay(cache.giveaways.filter(isActive));
  }

  function getById(id) {
    if (!id) return null;
    const all = getAll();
    for (let i = 0; i < all.length; i++) {
      if (String(all[i]._id) === String(id)) return all[i];
    }
    return null;
  }

  function isStale() {
    const cache = readCache();
    return !cache || !cache.fetchedAt || Date.now() - cache.fetchedAt > TTL_MS;
  }

  // --- init -------------------------------------------------------------------

  if (isStale()) fetchGiveaways();

  return {
    getAll: getAll,
    getById: getById,
    isStale: isStale,
    refresh: fetchGiveaways,
    ready: function () {
      return inFlight || Promise.resolve();
    },
  };
})();
